import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {createRequestOption, createRequestBody} from '../../shared/util';
import {environment} from '../../../environments/environment';
import {TransactionType} from '../../shared/model/enumerations';
import {ProductStatus} from '../../shared/model/enumerations/product-status.model';
import { map } from 'rxjs/operators';
import { ISetting } from 'src/app/shared/model';
import { Constant } from 'src/app/core/constant';

type EntityResponseType = HttpResponse<any>;
type EntityArrayResponseType = HttpResponse<any[]>;

@Injectable({ providedIn: 'root' })
export class ProductService {
  public resourceUrl = environment.SERVER_API_URL + 'api/product/';

  constructor(protected http: HttpClient) {}

  create(product: any): Observable<EntityResponseType> {
    return this.http.post<any>(this.resourceUrl, product, { observe: 'response' });
  }

  update(product: any): Observable<EntityResponseType> {
    return this.http.put<any>(environment.SERVER_API_URL +`api/product/${product.id}`, product, { observe: 'response' });
  }
  approve(productId: any, body: any): Observable<EntityResponseType> {
    return this.http.patch<any>(environment.SERVER_API_URL +`api/product/accept/${productId}`, body, { observe: 'response' });
  }
  deactivate(id: number): Observable<EntityResponseType> {
    const status = ProductStatus.CANCELLED;
    return this.http.get(`${this.resourceUrl}/${id}/changeStatus/${status}`, { observe: 'response' });
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http.get<any>(`${this.resourceUrl}${id}`, { observe: 'response' });
  }
  queryProducts(req?: any): Observable<EntityArrayResponseType> {
    const body = createRequestBody(req);
    return this.http.post<any[]>(this.resourceUrl + 'list/', body,  {  observe: 'response' });
  }
  queryFund(body?: any): Observable<EntityArrayResponseType> {
    const req = createRequestBody(body);
    return this.http.post<any[]>(this.resourceUrl + 'list/', req,  {  observe: 'response' });
  }
  queryActiveFund(body?: any): Observable<EntityArrayResponseType> {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/active-product', body,  {  observe: 'response' });
  }
  findProgram(id: number): Observable<EntityResponseType> {
    return this.http.get<any>(`${environment.SERVER_API_URL}api/automaticInvestmentProgram/${id}`, { observe: 'response' });
  }
  querySIP(body?: any): Observable<EntityArrayResponseType> {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/automaticInvestmentProgram/list/', body, {  observe: 'response' });
  }
  createProgram(body): Observable<EntityResponseType> {
    return this.http.post<any>(`${environment.SERVER_API_URL}api/automaticInvestmentProgram/`, body, { observe: 'response' });
  }
  updateProgram(body): Observable<EntityResponseType> {
    return this.http.put<any>(`${environment.SERVER_API_URL}api/automaticInvestmentProgram/${body.id}`, body, { observe: 'response' });
  }
  deleteProgram(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${environment.SERVER_API_URL}api/automaticInvestmentProgram/${id}`, { observe: 'response' });
  }
  queryBond(body?: any): Observable<EntityArrayResponseType> {
    return this.http.post<any[]>(this.resourceUrl + 'list/', body, { observe: 'response' });
  }
  queryCertificate(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any[]>(this.resourceUrl + 'certificate', { params: options, observe: 'response' });
  }
  queryUnitBond(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any[]>(this.resourceUrl + 'bond-issuer', { params: options, observe: 'response' });
  }
  queryDividend(body?: any): Observable<EntityArrayResponseType> {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productDividendDate/list/', body , {observe: 'response' });
  }
  queryGoldType(req?: any) {
    const options = createRequestOption(req);
    return this.http.get(this.resourceUrl + 'gold/list', {  params: options});
  }
  queryByType(productType, req?: any) {
    let type = '';
    let body: any = createRequestBody(req);
    body.typeCodes = req.searchParams.typeCodes || [];
    switch (productType) {
      case 'fund':
        body.typeCodes = [ Constant.CODE_NEW_FUND, Constant.CODE_TRADING_FUND ];
        type = 'queryFund';
        break;
      case 'bond':
        body.typeCodes = [ Constant.CODE_MATURITY_BOND, Constant.CODE_FIXED_BOND, Constant.CODE_FLEXIBLE_BOND];
        type = 'queryBond';
        break;
      case 'certificate':
        body.typeCodes = [ Constant.CODE_FIXED_INTEREST_ON_FIXED_CD, Constant.CODE_FLEXIBLE_INTEREST_ON_FIXED_CD ];
        type = 'queryCertificate';
        break;
      case 'bond-issuer':
        type = 'queryUnitBond';
        break;
      case 'dividend':
        type = 'queryDividend';
        break;
      case 'sip':
        delete body.statusId;
        delete body.typeCodes;
        type = 'querySIP';
        break;
    }
    return this[type](body)
    .pipe(map((res: any) => {
      const result = res.body.data;
      return result;
    }));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http.get<any[]>(this.resourceUrl, { params: options, observe: 'response' });
  }
  queryConfig(req?: any) {
    const options = createRequestOption(req);
    return this.http.get<ISetting[]>(environment.SERVER_API_URL + 'api/config/', { params: options, observe: 'response' })
    .pipe(map((res: any) => {
      const result = res.body.data;
      return result;
    }));
  }
  editConfig(config) {
    return this.http.put<ISetting>(environment.SERVER_API_URL + 'api/config/' + config.id, config, { observe: 'response' });
  }
  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }
  hidden(id: number, body): Observable<HttpResponse<{}>> {
    return this.http.patch(`${this.resourceUrl}updateProductStatus/${id}`, body, { observe: 'response' });
  }
  getDecreaseFeeList(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productFeeDiscount/list/', body,  {  observe: 'response' });
  }
  saveDecreaseFeeList(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productFeeDiscount/updateBatch', body,  {  observe: 'response' });
  }
  updateNAV(formData) {
    return this.http.post<any>(environment.SERVER_API_URL + 'api/productNav/upload', formData, { observe: 'response' })
    .pipe(map((res: any) => {
      const result = res.body.data;
      return result;
    }));
  }
  getProductsInIssuer(id) {
    return this.http.get<any[]>(environment.SERVER_API_URL + 'api/product/productTransferDestination/' + id, {observe: 'response' });
  }
  getTransferFeeList(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productFee/list/', body,  {  observe: 'response' });
  }
  saveTransferFeeList(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productFee/updateBatch', body,  {  observe: 'response' });
  }
  deleteTransferFeeList(body) {
    return this.http.post(environment.SERVER_API_URL + 'api/productFee/deleteBatch',  body,  {  observe: 'response' });
  }

  findProductDividend(id: number): Observable<EntityResponseType> {
    return this.http.get<any>(environment.SERVER_API_URL + 'api/productDividendDate/' + id, { observe: 'response' });
  }
  saveProductDividend(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productDividendDate/', body,  {  observe: 'response' });
  }
  updateProductDividend(body) {
    return this.http.put<any[]>(environment.SERVER_API_URL + 'api/productDividendDate/'  + body.id, body,  {  observe: 'response' });
  }
  deleteProductDividend(id) {
    return this.http.delete(environment.SERVER_API_URL + 'api/productDividendDate/' + id,  {  observe: 'response' });
  }
  uploadFileNAV(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productNav/v2/upload', body,  {  observe: 'response' });
  }
  createRequestUploadNAV(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productNav/v2/nav-change/create', body,  {  observe: 'response' });
  }
  getListNAV(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productNav/v2/nav-change/list', body,  {  observe: 'response' });
  }
  getListDivident(body) {
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productDividendDate/list/', body,  {  observe: 'response' });
  }
  approveUploadNAV(body) {
    const options = createRequestOption(body);
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productNav/v2/nav-change/approve', body,  { params: options, observe: 'response' });
  }
  rejectUploadNAV(body) {
    const options = createRequestOption(body);
    return this.http.post<any[]>(environment.SERVER_API_URL + 'api/productNav/v2/nav-change/reject', body,  { params: options, observe: 'response' });
  }

  getProductDocuments(body?: any) {
    const options = createRequestOption(body);
    return this.http.get(environment.SERVER_API_URL + 'api/product-document/list/', { params: options});
  }

  addProductDocument(body?: any) {
    const req = createRequestBody(body);
    return this.http.post(environment.SERVER_API_URL + 'api/product-document/', req,  {  observe: 'response' });
  }
  updateProductDocument(body?: any) {
    const req = createRequestBody(body);
    return this.http.put(environment.SERVER_API_URL + 'api/product-document/' + body.id, req,  {  observe: 'response' });
  }
  deleteProductDocument(id?: any) {
    return this.http.delete(environment.SERVER_API_URL + 'api/product-document/' + id);
  }
  getProductFeeTemp(body?: any) {
    return this.http.post(environment.SERVER_API_URL + 'api/productFeeTemp/list/', body);
  }
  createProductFeeTemp(body?: any) {
    return this.http.post(environment.SERVER_API_URL + 'api/productFeeTemp/', body);
  }
  deleteProductFeeTemp(body?: any) {
    return this.http.post(environment.SERVER_API_URL + 'api/productFeeTemp/delete' ,body);
  }
}
